import React, { useState } from "react";
import { Calculator, Calendar, Mail } from "lucide-react";

export default function SoccerAgeCalculator() {
  const [birthDate, setBirthDate] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  function getSoccerAgeGroup(dateOfBirth: Date): string {
    const birthDate = new Date(dateOfBirth);

    // Age group mappings based on birth date ranges
    const ageGroups = [
      {
        start: new Date("2021-08-01"),
        end: new Date("2026-01-01"),
        group:
          "Too Young for our Travel teams for Fall 2026 - Spring 2027. Please look at https://swmkickers.org/academy/ for more options",
      },
      {
        start: new Date("2020-08-01"),
        end: new Date("2021-07-31"),
        group: "U6",
      },
      {
        start: new Date("2019-08-01"),
        end: new Date("2020-07-31"),
        group: "U7",
      },
      {
        start: new Date("2018-08-01"),
        end: new Date("2019-07-31"),
        group: "U8",
      },
      {
        start: new Date("2017-08-01"),
        end: new Date("2018-07-31"),
        group: "U9",
      },
      {
        start: new Date("2016-08-01"),
        end: new Date("2017-07-31"),
        group: "U10",
      },
      {
        start: new Date("2015-08-01"),
        end: new Date("2016-07-31"),
        group: "U11",
      },
      {
        start: new Date("2014-08-01"),
        end: new Date("2015-07-31"),
        group: "U12",
      },
      {
        start: new Date("2013-08-01"),
        end: new Date("2014-07-31"),
        group: "U13",
      },
      {
        start: new Date("2012-08-01"),
        end: new Date("2013-07-31"),
        group: "U14",
      },
      {
        start: new Date("2011-08-01"),
        end: new Date("2012-07-31"),
        group: "U15",
      },
      {
        start: new Date("2010-08-01"),
        end: new Date("2011-07-31"),
        group: "U16",
      },
      {
        start: new Date("2009-08-01"),
        end: new Date("2010-07-31"),
        group: "U17",
      },
      {
        start: new Date("2008-08-01"),
        end: new Date("2009-07-31"),
        group: "U18",
      },
      {
        start: new Date("2007-08-01"),
        end: new Date("2008-07-31"),
        group: "U19",
      },
      {
        start: new Date("1920-01-01"),
        end: new Date("2007-07-31"),
        group: "Too Old for our Travel teams for Fall 2026 - Spring 2027",
      },
    ];

    // Find the matching age group
    for (const ageGroup of ageGroups) {
      if (birthDate >= ageGroup.start && birthDate <= ageGroup.end) {
        return ageGroup.group;
      }
    }

    // If no match found, return appropriate message
    return "Please enter a valid date of birth";
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setBirthDate(date);
    setAgeGroup(getSoccerAgeGroup(new Date(date)));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-8 px-4 shadow-lg">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-2xl md:text-3xl font-bold">
              SWM Age Division Calculator
            </h1>
          </div>
          <p className="text-base md:text-lg opacity-90">
            Find your child's age division for Fall 2026 & Spring 2027 Soccer
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto p-6 mt-8">
        {/* Calculator Card */}
        <div className="bg-white rounded-xl shadow-xl p-8 mb-8 border-t-4 border-blue-500">
          <div className="flex items-center gap-2 mb-6">
            <Calculator className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800">
              Calculate Division
            </h2>
          </div>

          <div className="space-y-6">
            {/* Birth Date Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" />
                Date of Birth
              </label>
              <input
                type="date"
                value={birthDate}
                onChange={handleDateChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Result */}
            {ageGroup && (
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-white rounded-lg border-l-4 border-blue-500">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
                  Your Age Division for Fall 2026 / Spring 2027 Season:
                </h3>
                <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-2">
                  {ageGroup}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-500">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg md:text-xl font-semibold text-gray-800">
              Have Questions?
            </h3>
          </div>
          <p className="text-gray-600 text-sm md:text-base">
            If you have any questions about age divisions or need assistance,
            please reach out to us at{" "}
            <a
              href="mailto:exampleswmemail@gmail.com"
              className="text-blue-600 hover:text-blue-800 font-medium underline"
            >
              exampleswmemail@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
